/**
 * Base API client for making HTTP requests
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function getAuthToken() {
  return localStorage.getItem('idToken');
}

export function setAuthToken(idToken, accessToken = null, refreshToken = null) {
  if (idToken) {
    localStorage.setItem('idToken', idToken);
    localStorage.setItem('authToken', idToken);
  } else {
    localStorage.removeItem('idToken');
    localStorage.removeItem('authToken');
  }
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
  } else {
    localStorage.removeItem('accessToken');
  }
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  } else {
    localStorage.removeItem('refreshToken');
  }
}

function clearAuth() {
  setAuthToken(null, null, null);
  localStorage.removeItem('user');
}

/**
 * Attempt to refresh tokens using the stored refresh token.
 * Returns true if successful.
 */
async function attemptTokenRefresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (data.tokens?.idToken) {
      setAuthToken(data.tokens.idToken, data.tokens.accessToken, refreshToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Build fetch config from options
 */
function buildConfig(options) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const config = { ...options, headers };
  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }
  return config;
}

/**
 * Core fetch wrapper — handles JSON parsing and error throwing
 */
async function doFetch(url, config) {
  const response = await fetch(url, config);

  if (response.status === 204) return null;

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(data.message || 'An error occurred', response.status, data);
  }

  return data;
}

/**
 * Make an HTTP request with automatic token refresh on 401.
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    return await doFetch(url, buildConfig(options));
  } catch (error) {
    // On 401, try to refresh and retry once
    if (error instanceof ApiError && error.status === 401) {
      const refreshed = await attemptTokenRefresh();

      if (refreshed) {
        // Retry with new token
        try {
          return await doFetch(url, buildConfig(options));
        } catch (retryError) {
          // Retry also failed — clear auth and redirect to login
          clearAuth();
          window.location.href = '/login';
          throw retryError;
        }
      } else {
        // Refresh failed — clear auth and redirect to login
        clearAuth();
        window.location.href = '/login';
      }
    }

    if (error instanceof ApiError) throw error;

    throw new ApiError(error.message || 'Network error', 0, null);
  }
}

export function get(endpoint, options = {}) {
  return request(endpoint, { ...options, method: 'GET' });
}

export function post(endpoint, body, options = {}) {
  return request(endpoint, { ...options, method: 'POST', body });
}

export function put(endpoint, body, options = {}) {
  return request(endpoint, { ...options, method: 'PUT', body });
}

export function del(endpoint, options = {}) {
  return request(endpoint, { ...options, method: 'DELETE' });
}

export function patch(endpoint, body, options = {}) {
  return request(endpoint, { ...options, method: 'PATCH', body });
}

export default { get, post, put, delete: del, patch, setAuthToken };
